import { supabase } from '../lib/supabaseClient';
import { PointAdjustment, PointAdjustmentReason, ForfaitType } from '../types';

// Table name constant
const TABLE = 'point_adjustments';
const MATCHES_TABLE = 'matches';

export interface CreateAdjustmentInput {
    season: string;
    leagueGroupId: string;
    teamId: string;
    matchId?: string | null;
    reason: PointAdjustmentReason;
    deltaPoints: number;
    label: string;
    details?: string;
    source?: string;
}

export async function listAdjustments(leagueGroupId: string, season: string): Promise<PointAdjustment[]> {
    const { data, error } = await supabase
        .from(TABLE)
        .select('id, season, league_group_id, team_id, match_id, reason, delta_points, label, details, source, is_reversal, parent_adjustment_id, created_at, created_by')
        .eq('league_group_id', leagueGroupId)
        .eq('season', season)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(mapRow);
}

export async function createAdjustment(input: CreateAdjustmentInput): Promise<PointAdjustment> {
    const { data: authData, error: authErr } = await supabase.auth.getUser();
    if (authErr) throw authErr;
    const userId = authData.user?.id || null;
    const payload: any = {
        season: input.season,
        league_group_id: input.leagueGroupId,
        team_id: input.teamId,
        match_id: input.matchId || null,
        reason: input.reason,
        delta_points: input.deltaPoints,
        label: input.label,
        details: input.details || null,
        source: input.source || 'user',
        created_by: userId
    };
    const { data, error } = await supabase.from(TABLE).insert(payload).select('*').single();
    if (error) throw error;
    return mapRow(data);
}

export async function reverseAdjustment(id: string): Promise<PointAdjustment> {
    // Create a reversal entry referencing the original
    const { data: original, error: loadErr } = await supabase.from(TABLE).select('*').eq('id', id).single();
    if (loadErr) throw loadErr;
    if (original.is_reversal) throw new Error('Impossible d\'annuler un ajustement déjà annulé.');
    const { data: authData, error: authErr } = await supabase.auth.getUser();
    if (authErr) throw authErr;
    const userId = authData.user?.id || null;

    const reversal = {
        season: original.season,
        league_group_id: original.league_group_id,
        team_id: original.team_id,
        match_id: original.match_id,
        reason: original.reason === PointAdjustmentReason.REVERSAL ? original.reason : original.reason, // keep same base reason
        delta_points: -original.delta_points,
        label: 'Annulation ' + original.label,
        details: 'Reversal of ' + original.id,
        source: 'system',
        is_reversal: true,
        parent_adjustment_id: original.id,
        created_by: userId
    };
    const { data, error } = await supabase.from(TABLE).insert(reversal).select('*').single();
    if (error) throw error;
    return mapRow(data);
}

interface DeclareForfaitInput {
    matchId: string;
    forfaitType: ForfaitType;
    season: string;
    leagueGroupId: string;
    homeTeamId: string;
    awayTeamId: string;
    technicalHome: number;
    technicalAway: number;
    deductionTeams: string[]; // team ids to penalize
    pointsDelta: number; // negative value expected
    reason: string;
}

export async function declareForfait(input: DeclareForfaitInput): Promise<{ adjustments: PointAdjustment[] }> {
    const { data: authData, error: authErr } = await supabase.auth.getUser();
    if (authErr) throw authErr;
    const userId = authData.user?.id || null;
    // Prefer using the PostgreSQL function if available
    const { data, error } = await supabase.rpc('declare_forfait', {
        p_match_id: input.matchId,
        p_forfait_type: input.forfaitType,
        p_season: input.season,
        p_league_group_id: input.leagueGroupId,
        p_home_team_id: input.homeTeamId,
        p_away_team_id: input.awayTeamId,
        p_technical_home: input.technicalHome,
        p_technical_away: input.technicalAway,
        p_deduction_teams: input.deductionTeams,
        p_points_delta: input.pointsDelta,
        p_reason: input.reason,
        p_user_id: userId
    });
    if (error) throw error;
    return { adjustments: (data || []).map(mapRow) };
}

// Map DB row (snake_case) to frontend (camelCase)
function mapRow(row: any): PointAdjustment {
    return {
        id: row.id,
        season: row.season,
        leagueGroupId: row.league_group_id,
        teamId: row.team_id,
        matchId: row.match_id,
        reason: row.reason as PointAdjustmentReason,
        deltaPoints: row.delta_points,
        label: row.label,
        details: row.details || undefined,
        source: row.source || undefined,
        isReversal: row.is_reversal || false,
        parentAdjustmentId: row.parent_adjustment_id || undefined,
        createdAt: row.created_at,
        createdBy: row.created_by,
        createdByName: row.created_by_name,
        reversedAt: row.reversed_at,
        reversedBy: row.reversed_by,
        reversedByName: row.reversed_by_name
    };
}
